import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ConfigKeyFE_ComplaintsTagId, ConfigKeyFE_DiseasesViewsTagId, DropDown_Tag, DropDown_Variable, ModuleFE_Tag, ModuleFE_Variable } from '@app/hcs-frontend';
import { DBItemDropDownMultiSelector } from '@nu-art/thunderstorm-frontend/components/_TS_MultiSelect/DBItemDropDownMultiSelector';
import { FadeScrollWrapper, ICONS } from '@app/styles';
import { _className, AwaitModules, Button, Grid, TS_MultiSelect_V2, TS_PropRenderer, TS_Radio } from '@nu-art/thunderstorm-frontend';
import { exists, flatArray, MUSTNeverHappenException } from '@nu-art/ts-common';
import { ModuleFE_DiseaseView, ModuleFE_MultiVar } from '../../../../../../../_entity.js';
import './Editor_MultiVar_Description.scss';
import { DropDown_DefaultCaret } from '@app/styles/frontend/ui/consts';
import { profileEditor_OpenVariable } from '../../../../consts.js';
const isComplaintVariable = (varId) => {
    const variable = ModuleFE_Variable.cache.unique(varId);
    if (!variable)
        return false;
    const complaintTagId = ConfigKeyFE_ComplaintsTagId.get();
    return variable.tagIds.some(g => g.value.includes(complaintTagId));
};
const MultiSelect_DiseaseViewTag = TS_MultiSelect_V2.prepare(DBItemDropDownMultiSelector.propsV3({
    module: ModuleFE_Tag,
    itemRenderer: (item, onDelete) => !item
        ? _jsx(_Fragment, { children: "Not Found" })
        : _jsxs(_Fragment, { children: [_jsx(ICONS.x.component, { onClick: onDelete }), _jsx("div", { className: 'node-data', children: item.label })] }),
    uiSelector: ({ queryFilter, onSelected }) => _jsx(DropDown_Tag.selectable, { queryFilter: queryFilter, onSelected: onSelected, placeholder: 'Select Disease View' }),
}));
const Field_DiseaseView = (props) => {
    const diseaseViewsTagId = ConfigKeyFE_DiseasesViewsTagId.get();
    const groupIndex = props.editable.item.findIndex(g => g?.key === diseaseViewsTagId);
    if (groupIndex === -1)
        throw new MUSTNeverHappenException('first column in MV must have a tag group for disease views!');
    const editable = props.editable.editProp(groupIndex, {});
    const allowedTagIds = new Set(flatArray(ModuleFE_DiseaseView.cache.all().map(dv => dv.enforcedTags)));
    return _jsx(TS_PropRenderer.Horizontal, { label: 'Disease View', children: _jsx(MultiSelect_DiseaseViewTag, { editable: editable, prop: 'value', disabled: !props.editMode, itemsDirection: 'vertical', itemFilter: id => allowedTagIds.has(id) }) });
};
const Field_Variant = (props) => {
    if (!props.variantGroupId)
        return;
    const variantTag = ModuleFE_Tag.cache.unique(props.variantGroupId);
    if (!variantTag)
        throw new MUSTNeverHappenException(`Variant group with id ${props.variantGroupId} does not exist`);
    let groupIndex = props.editable.item.findIndex(g => g?.key === props.variantGroupId);
    if (groupIndex === -1)
        groupIndex = props.editable.item.length;
    const editable = props.editable.editProp(groupIndex, { key: variantTag._id, value: [] });
    return _jsx(TS_PropRenderer.Horizontal, { label: variantTag.label, children: _jsx(DropDown_Tag.selectable, { canUnselect: true, disabled: !props.editMode, selected: editable.item.value?.[0], onSelected: tag => {
                if (!tag)
                    return props.editable.removeArrayItem(groupIndex);
                editable.updateObj({ value: [tag._id] });
            }, placeholder: 'Select Variant', queryFilter: tag => tag.tagIds.includes(variantTag._id) }) });
};
const Field_ChiefComplaint = (props) => {
    const varId = props.editable.item.variableId;
    if (!varId || !isComplaintVariable(varId))
        return;
    const chiefComplaintIds = props.editableDP.item.chiefComplaintIds ?? [];
    const isChiefComplaint = exists(varId) && chiefComplaintIds.includes(varId);
    return _jsx(TS_PropRenderer.Horizontal, { label: 'Is Chief Complaint?', children: _jsx(TS_Radio, { values: [true, false], disabled: !props.editMode, groupName: 'chief-complaint', labelRenderer: val => val ? 'Yes' : 'No', checked: isChiefComplaint, onCheck: checked => {
                if (checked === isChiefComplaint)
                    return;
                const newIds = checked
                    ? [...chiefComplaintIds, varId]
                    : chiefComplaintIds.filter(id => id !== varId);
                props.editableDP.updateObj({ chiefComplaintIds: newIds });
            } }) });
};
const Field_Variable = (props) => {
    const variable = ModuleFE_Variable.cache.unique(props.editable.item.variableId);
    return _jsxs(TS_PropRenderer.Horizontal, { label: 'Variable', children: [_jsx("div", { className: 'e__multi-var__general-value variable', onClick: () => profileEditor_OpenVariable(variable), children: variable?.name ?? 'N/A' }), props.children] });
};
const Field_MainVariable = (props) => {
    const editableTagIds = props.editable.editProp('tagIds', []);
    return _jsxs(Grid, { className: 'e__multi-var-variable', children: [_jsx(Field_Variable, { editMode: props.editMode, editable: props.editable }), _jsx(Field_DiseaseView, { editMode: props.editMode, editable: editableTagIds }), _jsx(Field_Variant, { editMode: props.editMode, editable: editableTagIds, variantGroupId: props.editableDP.item.variantsGroupId }), _jsx(Field_ChiefComplaint, { editMode: props.editMode, editable: props.editable, editableDP: props.editableDP })] });
};
const Field_ColumnVariable = (props) => {
    return _jsx(Grid, { className: 'e__multi-var-variable secondary', children: _jsx(Field_Variable, { editMode: props.editMode, editable: props.editable, children: props.editMode && _jsx(Button, { variant: 'dangerous', onClick: async () => {
                    if (!props.multiVarId)
                        return props.editable.delete();
                    await ModuleFE_MultiVar._v1.deleteMultiVarColumn({
                        variableId: props.editable.item.variableId,
                        multiVarId: props.multiVarId,
                    }).executeSync();
                }, children: _jsx(ICONS.bin.component, {}) }) }) });
};
const generateMVV = (variable, index, defaultViewTagId) => {
    const newMvv = {
        variableId: variable._id,
        tagIds: variable.tagIds
    };
    //Add tag groups for main variable
    if (index === 0) {
        newMvv.tagIds.push({
            key: ConfigKeyFE_DiseasesViewsTagId.get(),
            value: defaultViewTagId ? [defaultViewTagId] : [],
        });
    }
    return newMvv;
};
export const Editor_MultiVar_Description = (props) => {
    const className = _className('e__multi-var__description', props.editMode && 'edit-mode');
    const editableMVVs = props.editable.editProp('variables', []);
    const currentVarIds = new Set(editableMVVs.item.map(mvv => mvv.variableId));
    const defaultViewTagId = props.defaultDiseaseViewId
        ? ModuleFE_DiseaseView.cache.unique(props.defaultDiseaseViewId)?.enforcedTags[0]
        : undefined;
    return _jsx(AwaitModules, { modules: [ModuleFE_Variable, ModuleFE_Tag], children: _jsxs(FadeScrollWrapper, { className: className, children: [_jsx(Grid, { children: editableMVVs.item.map((_, index) => {
                        const editable = editableMVVs.editProp(index, {});
                        editable.setOnDelete(async () => editableMVVs.removeArrayItem(index));
                        if (!index)
                            return _jsx(Field_MainVariable, { editable: editable, editMode: props.editMode, editableDP: props.editableDP }, _?.variableId ?? index);
                        return _jsx(Field_ColumnVariable, { editable: editable, editMode: props.editMode, multiVarId: props.editable.item._id }, _?.variableId ?? index);
                    }) }), props.editMode && _jsx(DropDown_Variable, { selected: undefined, limitItems: 50, placeholder: 'Add new variable', caret: DropDown_DefaultCaret, queryFilter: variable => !currentVarIds.has(variable._id), onSelected: variable => {
                        editableMVVs.updateArrayAt(generateMVV(variable, editableMVVs.item.length, defaultViewTagId));
                    } })] }) });
};
//# sourceMappingURL=Editor_MultiVar_Description.js.map